import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { getVerificationMonth, listCommissionPayments, money } from "./commissionsApi";

type PaymentRow = Awaited<ReturnType<typeof listCommissionPayments>>["rows"][number];

const PAYMENT_COLUMNS: HubDataTableColumn<PaymentRow>[] = [
  {
    id: "paid_on",
    header: "Date",
    overflow: "nowrap",
    sortValue: (row) => String(row.paid_on),
    cell: (row) => String(row.paid_on),
  },
  {
    id: "rep",
    header: "Rep",
    sortValue: (row) => String(row.rep_agency_name ?? ""),
    cell: (row) => (
      <RepAgencyDetailLink
        repAgencyId={row.rep_agency_id != null ? Number(row.rep_agency_id) : null}
        repAgencyName={row.rep_agency_name != null ? String(row.rep_agency_name) : null}
      />
    ),
  },
  {
    id: "amount",
    header: "Amount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.amount),
    cell: (row) => money(row.amount as number),
  },
  {
    id: "notes",
    header: "Notes",
    overflow: "truncate",
    sortValue: (row) => String(row.notes ?? ""),
    cell: (row) => String(row.notes ?? "—"),
  },
];

export function CommissionReconciliationPage() {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    const previous = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return previous.toISOString().slice(0, 7);
  });
  const [basePaid, setBasePaid] = useState("");
  const [correction, setCorrection] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [actual, setActual] = useState("");
  const [paymentRep, setPaymentRep] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");

  const verificationQuery = useQuery({
    queryKey: ["commissions", "verification", month],
    queryFn: () => getVerificationMonth(`${month}-01`),
  });
  const paymentsQuery = useQuery({
    queryKey: ["commissions", "payments"],
    queryFn: listCommissionPayments,
  });

  const saveMutation = useMutation({
    mutationFn: (confirm: boolean) =>
      apiFetch(`/api/commissions/verification/${month}-01`, {
        method: "PATCH",
        body: {
          base_paid: basePaid.trim() === "" ? null : Number.parseFloat(basePaid),
          correction_amount: correction.trim() === "" ? null : Number.parseFloat(correction),
          notes: notes || null,
          scheduled_to_pay: scheduled.trim() === "" ? null : Number.parseFloat(scheduled),
          actual_paid: actual.trim() === "" ? null : Number.parseFloat(actual),
          confirm,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "verification", month] });
    },
  });

  const paymentMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/commissions/payments", {
        method: "POST",
        body: {
          rep_agency_name: paymentRep || null,
          amount: Number.parseFloat(paymentAmount),
          paid_on: paymentDate,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "payments"] });
      setPaymentAmount("");
    },
  });

  const computed = verificationQuery.data?.computed;
  const row = verificationQuery.data?.row;

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Enter human verification inputs. Delta and difference should be zero before paying reps.">
        Reconciliation
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Verification month</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="ver-month">Month</Label>
            <Input id="ver-month" type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
          </div>
          {computed != null ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-sm">
              <div>Total deposits: {money(computed.totalDeposits)}</div>
              <div>Shipping: {money(computed.shippingCosts)}</div>
              <div>Standard basis: {money(computed.standardCommissionsBasis)}</div>
              <div>Repless: {money(computed.replessCommissions)}</div>
              <div>Regional stocking: {money(computed.regionalStockingCommissions)}</div>
              <div>Stored delta: {row?.delta != null ? money(row.delta as number) : "—"}</div>
            </div>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label htmlFor="base-paid">Base paid</Label>
              <Input id="base-paid" value={basePaid} onChange={(event) => setBasePaid(event.target.value)} />
            </div>
            <div>
              <Label htmlFor="correction">Correction</Label>
              <Input id="correction" value={correction} onChange={(event) => setCorrection(event.target.value)} />
            </div>
            <div>
              <Label htmlFor="scheduled">Scheduled to pay</Label>
              <Input id="scheduled" value={scheduled} onChange={(event) => setScheduled(event.target.value)} />
            </div>
            <div>
              <Label htmlFor="actual">Actual paid</Label>
              <Input id="actual" value={actual} onChange={(event) => setActual(event.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ver-notes">Notes</Label>
              <Input id="ver-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={() => saveMutation.mutate(false)} disabled={saveMutation.isPending}>
              Save
            </Button>
            <Button type="button" onClick={() => saveMutation.mutate(true)} disabled={saveMutation.isPending}>
              Save and confirm
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Record payment</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="pay-rep">Rep</Label>
            <Input id="pay-rep" value={paymentRep} onChange={(event) => setPaymentRep(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="pay-amount">Amount</Label>
            <Input id="pay-amount" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="pay-date">Date</Label>
            <Input id="pay-date" type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
          </div>
          <Button
            type="button"
            onClick={() => paymentMutation.mutate()}
            disabled={paymentMutation.isPending || paymentAmount.trim() === "" || paymentDate.trim() === ""}
          >
            Add payment
          </Button>
        </CardBody>
      </Card>

      <HubDataTable
        title="Payments"
        columns={PAYMENT_COLUMNS}
        rows={paymentsQuery.data?.rows ?? []}
        rowKey={(row) => String(row.id)}
        loading={paymentsQuery.isPending}
        loadingMessage="Loading payments…"
        error={paymentsQuery.isError ? "Could not load payments." : null}
        empty="No payments recorded."
      />
    </div>
  );
}
